/* eslint-disable consistent-return */
import React, { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Box, BoxSection } from 'components/_new/Box';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'components/_new/Button';
import { useNavigate } from 'react-router-dom';
import { useGlobal } from 'contexts/globalContext';
import { useScope } from 'contexts/scopeContext';
import { TimelineEventItem, TimelineEventTypeEnum } from 'types/Timeline';
import { timelineApi } from 'api/timeline';
import { Field } from 'components/_new/Field';
import { InputTextArea } from 'components/_new/InputTextArea';
import { InputText } from 'components/_new/InputText';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Dropdown } from 'components/_new/Dropdown';
import Calendar from 'components/_common/forms/Calendar';
import { Calendar as CalendarOriginal } from 'primereact/calendar';
import dayjs from 'dayjs';
import { ApplicationRole, ApplicationRoleEnum } from 'types/Application';
import { urlValidator } from 'utils/urlValidator';
import { DraftEditor } from 'components/_common/DraftEditor/DraftEditor';
import { InputNumber } from 'components/_new/InputNumber';

type ModifyEventTimelineFormType = {
	summary: string;
	intro: string;
	description: string;
	type: TimelineEventTypeEnum;
	forApplicationRole: ApplicationRole | null;
	dayDate?: string;
	startAt?: Date;
	endAt?: Date;
	mandatory: boolean;
	requireConfirmation: boolean;
	onlineMeetingUrl: string | null;
	participantLimit: number | null;
};

type ModifyTimelineEventFormProps = { timelineEvent?: TimelineEventItem; onSave?: () => void };

export const ModifyTimelineEventForm = ({ timelineEvent, onSave }: ModifyTimelineEventFormProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { toastRef } = useGlobal();
	const { panelPath } = useProgramPanel();
	const { currentProgram: data } = useScope();

	const { control, handleSubmit, watch, setValue } = useForm<ModifyEventTimelineFormType>({
		defaultValues: {
			summary: timelineEvent?.summary || '',
			intro: timelineEvent?.intro || '',
			description: timelineEvent?.description || '',
			type: timelineEvent?.type
				? (timelineEvent.type as unknown as TimelineEventTypeEnum)
				: TimelineEventTypeEnum.EVENT,
			forApplicationRole: timelineEvent?.forApplicationRole || null,
			dayDate: timelineEvent?.dayDate ?? undefined,
			startAt: timelineEvent?.startAt ? new Date(timelineEvent.startAt) : undefined,
			endAt: timelineEvent?.endAt ? new Date(timelineEvent.endAt) : undefined,
			mandatory: timelineEvent?.mandatory || false,
			requireConfirmation: timelineEvent?.requireConfirmation || false,
			onlineMeetingUrl: timelineEvent?.onlineMeetingUrl || '',
			participantLimit: timelineEvent?.participantLimit || undefined,
		},
	});

	const { mutate: addTimelineEventeMutate, isLoading: addArticleLoading } = useMutation(
		(value: ModifyEventTimelineFormType) => timelineApi.program.addTimelineEvent(Number(data?.id), value),
		{
			onSuccess: (data) => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('timeline.programPanel.modify.addedSuccessfully'),
				});
				navigate({ pathname: `/${panelPath}/timeline/edit/${data.eventId}` });
				onSave?.();
			},
		}
	);

	const { mutate: editTimelineEventMutate, isLoading: editArticleLoading } = useMutation(
		(value: ModifyEventTimelineFormType) =>
			timelineApi.program.editTimelineEvent(Number(data?.id), Number(timelineEvent?.eventId), value),
		{
			onSuccess: (data) => {
				toastRef?.current?.show({
					severity: 'success',
					life: 3000,
					summary: t('misc.success'),
					detail: t('timeline.programPanel.modify.updatedSuccessfully'),
				});
				navigate({ pathname: `/${panelPath}/timeline/details/${data.eventId}` });
				onSave?.();
			},
		}
	);

	const handleSubmitForm = handleSubmit(
		({
			summary,
			intro,
			description,
			type,
			forApplicationRole,
			startAt,
			endAt,
			dayDate,
			mandatory,
			requireConfirmation,
			onlineMeetingUrl,
			participantLimit,
		}) => {
			const formatedDayDate = dayDate ? dayjs(dayDate).startOf('day').format('YYYY-MM-DD') : undefined;
			const data = {
				summary,
				intro,
				description,
				type,
				forApplicationRole,
				startAt,
				endAt,
				dayDate: formatedDayDate,
				mandatory,
				requireConfirmation,
				onlineMeetingUrl: onlineMeetingUrl && onlineMeetingUrl.length > 0 ? onlineMeetingUrl : null,
				participantLimit,
			};
			if (mandatory || data.participantLimit === 0 || !requireConfirmation) {
				data.participantLimit = null;
			}
			if (timelineEvent) {
				editTimelineEventMutate(data);
			} else {
				addTimelineEventeMutate(data);
			}
		}
	);

	const type = watch('type');
	const startAt = watch('startAt');
	const endAt = watch('endAt');
	const minDate = startAt ? dayjs(startAt).toDate() : undefined;
	const mandatory = watch('mandatory');
	const requireConfirmation = watch('requireConfirmation');
	const disableParticipationLimit = mandatory === true || requireConfirmation === false;
	const isFirstStartAtEffect = useRef(true);
	const isEndAtAutoSyncEnabled = useRef(!timelineEvent?.endAt);

	useEffect(() => {
		if (isFirstStartAtEffect.current) {
			isFirstStartAtEffect.current = false;
			return;
		}

		if (!startAt) {
			return;
		}

		if ((!endAt && isEndAtAutoSyncEnabled.current) || (endAt && dayjs(startAt).isAfter(dayjs(endAt)))) {
			setValue('endAt', dayjs(startAt).add(1, 'hour').toDate(), { shouldValidate: true });
		}
	}, [startAt, endAt, setValue]);

	return (
		<form onSubmit={handleSubmitForm}>
			<div className="flex flex-column gap-4 sm:flex-row">
				<div className="flex-1">
					<Box variant="white-bordered" vertical>
						<BoxSection header={t('timeline.programPanel.modify.contentSection.title')} className="w-full">
							<Controller
								control={control}
								name="type"
								rules={{
									required: {
										value: true,
										message: t('misc.forms.fieldIsRequired'),
									},
								}}
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<Field
										label={t('timeline.programPanel.modify.mainOptionsSection.type.title')}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<Dropdown
											{...field}
											inputId={field.name}
											inputRef={ref}
											options={[
												{
													label: t(
														'timeline.programPanel.modify.mainOptionsSection.type.options.event'
													),
													value: TimelineEventTypeEnum.EVENT,
												},
												{
													label: t(
														'timeline.programPanel.modify.mainOptionsSection.type.options.milestone'
													),
													value: TimelineEventTypeEnum.MILESTONE,
												},
											]}
										/>
									</Field>
								)}
							/>

							<Controller
								control={control}
								rules={{
									required: {
										value: true,
										message: t('misc.forms.fieldIsRequired'),
									},
									minLength: {
										value: 3,
										message: t('misc.forms.pleaseEnterMinChars', { min: 3 }),
									},
								}}
								name="summary"
								render={({ field, fieldState: { error } }) => (
									<Field label={t('timeline.item.summary')} errorMessage={error?.message}>
										<InputText {...field} max={120} />
									</Field>
								)}
							/>

							<Controller
								name="intro"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<Field label={t('timeline.item.intro')} errorMessage={error?.message}>
										<InputTextArea {...field} />
									</Field>
								)}
							/>

							<Controller
								name="description"
								control={control}
								render={({ field: { onChange }, fieldState: { error } }) => (
									<Field label={t('timeline.item.description')} errorMessage={error?.message}>
										<DraftEditor
											defaultValue={timelineEvent?.description || ''}
											onChange={(value) => {
												onChange(value);
											}}
											uploadRequestFunction={(file) =>
												timelineApi.program.uploadImage(Number(data?.id), file)
											}
										/>
									</Field>
								)}
							/>
						</BoxSection>
					</Box>
				</div>

				<div className="flex flex-column gap-2 w-300-auto">
					<Box variant="white-bordered">
						<BoxSection header={t('timeline.programPanel.modify.mainOptionsSection.title')}>
							<Controller
								control={control}
								name="forApplicationRole"
								render={({ field: { ref, ...field }, fieldState: { error } }) => (
									<Field
										label={t(
											'timeline.programPanel.modify.mainOptionsSection.forApplicationRole.title'
										)}
										htmlFor={field.name}
										errorMessage={error?.message}
									>
										<Dropdown
											{...field}
											inputId={field.name}
											inputRef={ref}
											withEmpty
											emptyLabel={t(
												'timeline.programPanel.modify.mainOptionsSection.forApplicationRole.options.all'
											)}
											emptyValue={null}
											options={[
												{
													label: t(
														'timeline.programPanel.modify.mainOptionsSection.forApplicationRole.options.mentee'
													),
													value: ApplicationRoleEnum.MENTEE,
												},
												{
													label: t(
														'timeline.programPanel.modify.mainOptionsSection.forApplicationRole.options.mentor'
													),
													value: ApplicationRoleEnum.MENTOR,
												},
											]}
										/>
									</Field>
								)}
							/>

							{type === TimelineEventTypeEnum.MILESTONE && (
								<Controller
									control={control}
									rules={{
										required: {
											value: type === TimelineEventTypeEnum.MILESTONE,
											message: t('misc.forms.fieldIsRequired'),
										},
									}}
									name="dayDate"
									render={({ field, fieldState: { error } }) => (
										<Field
											label={t('timeline.programPanel.modify.mainOptionsSection.time.date')}
											errorMessage={error?.message}
										>
											<CalendarOriginal
												{...field}
												value={field?.value ? new Date(field.value) : undefined}
												onChange={({ value }) => {
													if (value instanceof Date) {
														const dateString = dayjs(value).set('hour', 12).toDate();
														field?.onChange(dateString);
													}
												}}
											/>
										</Field>
									)}
								/>
							)}

							{type === TimelineEventTypeEnum.EVENT && (
								<>
									<Controller
										control={control}
										rules={{
											required: {
												value: type === TimelineEventTypeEnum.EVENT,
												message: t('misc.forms.fieldIsRequired'),
											},
											validate: {
												required: (value) => {
													if (type === TimelineEventTypeEnum.EVENT && !value) {
														return t('misc.forms.fieldIsRequired');
													}
												},
											},
										}}
										name="startAt"
										render={({ field }) => (
											<Field
												label={t(
													'timeline.programPanel.modify.mainOptionsSection.time.startAt'
												)}
											>
												<Calendar control={control} name="startAt" withHours />
											</Field>
										)}
									/>

									<Controller
										control={control}
										name="endAt"
										rules={{
											required: {
												value: type === TimelineEventTypeEnum.EVENT,
												message: t('misc.forms.fieldIsRequired'),
											},
											validate: {
												required: (value) => {
													if (type === TimelineEventTypeEnum.EVENT && !value) {
														return t('misc.forms.fieldIsRequired');
													}
												},
												minTime: (value) => {
													if (type === TimelineEventTypeEnum.EVENT) {
														const date = dayjs(value);
														if (dayjs(startAt).isAfter(date)) {
															return t(
																'timeline.programPanel.modify.mainOptionsSection.time.dateMustBeAfter'
															);
														}
													}
												},
											},
										}}
										render={() => (
											<Field
												label={t('timeline.programPanel.modify.mainOptionsSection.time.endAt')}
											>
												<Calendar
													control={control}
													name="endAt"
													withHours
													minDate={minDate}
													disabled={!startAt}
													onManualConfirm={() => {
														isEndAtAutoSyncEnabled.current = false;
													}}
												/>
											</Field>
										)}
									/>

									<Controller
										control={control}
										rules={{
											validate: {
												url: (value) => {
													if (
														type === TimelineEventTypeEnum.EVENT &&
														String(value).length > 0 &&
														!urlValidator(String(value))
													) {
														return t('userPanel.userFiles.file.invalidUrl');
													}
												},
											},
										}}
										name="onlineMeetingUrl"
										render={({ field, fieldState: { error } }) => (
											<Field
												label={t('timeline.item.onlineMeetingUrl')}
												errorMessage={error?.message}
											>
												<InputText {...field} max={500} />
											</Field>
										)}
									/>
								</>
							)}
						</BoxSection>

						<BoxSection header={t('timeline.programPanel.modify.optionsSection.title')}>
							<div className="flex flex-column gap-2">
								<label className="text-nowrap">
									<Controller
										control={control}
										name="mandatory"
										render={({ field }) => (
											<Checkbox
												{...field}
												checked={type === TimelineEventTypeEnum.MILESTONE ? false : field.value}
												className="mr-2"
												disabled={type === TimelineEventTypeEnum.MILESTONE}
											/>
										)}
									/>
									<span>{t('timeline.item.mandatory')}</span>
								</label>
								<label className="text-nowrap">
									<Controller
										control={control}
										name="requireConfirmation"
										render={({ field }) => (
											<Checkbox
												{...field}
												checked={type === TimelineEventTypeEnum.MILESTONE ? false : field.value}
												className="mr-2"
												disabled={type === TimelineEventTypeEnum.MILESTONE}
											/>
										)}
									/>
									<span>{t('timeline.item.requireConfirmation')}</span>
								</label>
								{!disableParticipationLimit && (
									<Controller
										control={control}
										name="participantLimit"
										render={({ field, fieldState: { error } }) => (
											<Field
												label={t('timeline.item.participationLimit')}
												errorMessage={error?.message}
												className="mt-4"
											>
												<label className="flex flex-row gap-1 align-items-center">
													<Checkbox
														checked={!field.value}
														onChange={({ checked }) => {
															setValue('participantLimit', !checked ? 20 : null);
														}}
													/>
													<span>{t('timeline.participationNoLimit')}</span>
												</label>
												{!(!field.value || Number(field.value) === 0) && (
													<InputNumber
														min={0}
														max={200}
														{...field}
														value={field.value ? Number(field.value) : 0}
														disabled={
															!field.value || Number(field.value) === 0 || mandatory
														}
													/>
												)}
											</Field>
										)}
									/>
								)}
							</div>
						</BoxSection>
					</Box>

					<Button
						label={timelineEvent ? t('actions.save') : t('actions.add')}
						loading={addArticleLoading || editArticleLoading}
						submit
						className="w-full"
					/>
				</div>
			</div>
		</form>
	);
};
